import React, { useState, useEffect } from 'react';
import pako from 'pako';
import { Property } from '@/types';
import { getPropertiesByIdsApi } from '@/services/propertyApiClient';
import { shortCodeMap } from '@/constants';
import { SpinnerIcon, Squares2X2Icon, MapPinIcon } from '@/components/Icons';
import { NewDashboardHeader } from '@/components/NewDashboardHeader';
import { Footer } from '@/components/Footer';
import { PropertyCard } from '@/components/broker/PropertyCard';
import { PublicMapView } from '@/components/PublicMapView';
import { BrokerBusinessCard } from '@/components/public/shared/BrokerBusinessCard';

export const SharedGroupView: React.FC<{ groupId: string }> = ({ groupId }) => {
  const [properties, setProperties] = useState<(Property & { sourceCollection?: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');

  useEffect(() => {
    const decodeAndFetch = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!groupId) throw new Error('No se proporcionó un identificador de grupo.');
        let base64 = groupId.replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4) base64 += '=';
        const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        const decompressed = pako.inflate(bytes, { to: 'string' });
        if (!decompressed) throw new Error('El enlace compartido no es válido.');

        const idToCollection = new Map<string, string>();
        const allIds: string[] = [];
        decompressed.split('|').forEach((part) => {
          const [shortCode, idsStr] = part.split(':');
          const coll = shortCodeMap[shortCode] || 'ciencuadras';
          if (idsStr) idsStr.split(',').forEach((id) => { if (id) { allIds.push(id); idToCollection.set(id, coll); } });
        });

        if (allIds.length === 0) throw new Error('El enlace de grupo no contiene inmuebles válidos.');
        const fetched = await getPropertiesByIdsApi(allIds, { isPublic: true });
        setProperties(fetched.map((p) => ({ ...p, sourceCollection: idToCollection.get(p.id) || 'ciencuadras' })));
      } catch (e: any) {
        setError(e?.message || 'Error al cargar el grupo.');
      } finally {
        setLoading(false);
      }
    };
    decodeAndFetch();
  }, [groupId]);

  const searchParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1] || '');
  const brokerName = searchParams.get('bn') || searchParams.get('bName');
  const brokerPhone = searchParams.get('bp') || searchParams.get('bPhone');

  const handleViewProperty = (p: Property) => {
    const source = (p as any).sourceCollection;
    const shortCode = Object.keys(shortCodeMap).find((k) => shortCodeMap[k] === source) || 'c';
    let url = `${window.location.origin}/share/${p.id}?collection=${shortCode}`;
    if (brokerName) url += `&bn=${encodeURIComponent(brokerName)}`;
    if (brokerPhone) url += `&bp=${encodeURIComponent(brokerPhone)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <NewDashboardHeader />
        <main className="flex-grow flex items-center justify-center"><SpinnerIcon className="w-12 h-12 text-sky-600 animate-spin" /></main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <NewDashboardHeader />
      <main className="flex-grow max-w-7xl mx-auto space-y-6 px-3 sm:px-6 lg:p-8 w-full">
        {brokerName && <BrokerBusinessCard compact={true} name={brokerName} phone={brokerPhone || undefined} />}
        {error ? (
          <div className="bg-red-50 text-red-700 p-6 rounded-2xl max-w-md mx-auto text-center shadow-sm">
            <p className="font-bold text-lg mb-2">Error al cargar inmuebles</p>
            <p className="text-sm">{error}</p>
          </div>
        ) : (
          <section className="bg-white p-4 sm:p-6 shadow-lg rounded-2xl">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-xl md:text-2xl font-bold text-gray-800">Inmuebles compartidos ({properties.length})</h1>
              <div className="flex items-center space-x-1 border rounded-lg p-0.5 bg-gray-50">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1.5 rounded-md flex items-center space-x-2 text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-sky-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  <Squares2X2Icon className="w-4 h-4" /><span>Cuadrícula</span>
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`px-3 py-1.5 rounded-md flex items-center space-x-2 text-sm font-medium transition-colors ${viewMode === 'map' ? 'bg-sky-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  <MapPinIcon className="w-4 h-4" /><span>Mapa</span>
                </button>
              </div>
            </div>

            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map((p) => (
                  <PropertyCard
                    key={p.id}
                    property={p}
                    isSelected={false}
                    onView={() => handleViewProperty(p)}
                    onShare={() => {}}
                    onToggleSelect={() => {}}
                    isCompact={false}
                    source={(p.sourceCollection as any) || 'ciencuadras'}
                    appUser={null}
                    onInterested={() => {}}
                    hideSpecialStatus={true}
                  />
                ))}
              </div>
            ) : (
              <div className="h-[600px] rounded-xl overflow-hidden shadow-inner">
                <PublicMapView properties={properties as Property[]} onViewProperty={handleViewProperty} />
              </div>
            )}
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};
